import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck, KeyRound, CheckCircle2, XCircle } from "lucide-react";
import { api, V1 } from "@/lib/api";
import { toast } from "sonner";

export default function PasswordReset() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [state, setState] = useState({ loading: true, valid: false, reason: null, email: null, name: null });
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) { setState({ loading: false, valid: false, reason: "not_found" }); return; }
    api.post(`${V1}/auth/verify-reset-token`, { token })
      .then((r) => setState({ loading: false, ...r.data }))
      .catch(() => setState({ loading: false, valid: false, reason: "not_found" }));
  }, [token]);

  const submit = async () => {
    if (pwd.length < 8) return toast.error("Şifre en az 8 karakter olmalı");
    if (pwd !== pwd2) return toast.error("Şifreler eşleşmiyor");
    setBusy(true);
    try {
      await api.post(`${V1}/auth/confirm-password-reset`, { token, new_password: pwd });
      setDone(true);
      toast.success("Şifreniz güncellendi. Giriş yapabilirsiniz.");
      setTimeout(() => navigate("/giris"), 2500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "İşlem başarısız");
    } finally { setBusy(false); }
  };

  if (state.loading) {
    return <div className="p-16 text-center text-muted-foreground">Bağlantı doğrulanıyor…</div>;
  }
  if (!state.valid) {
    const msg = state.reason === "used" ? "Bu bağlantı zaten kullanılmış." :
                state.reason === "expired" ? "Bu bağlantının süresi dolmuş." :
                "Bağlantı geçersiz veya bulunamadı.";
    return (
      <div className="mx-auto max-w-md px-4 py-16 text-center" data-testid="reset-invalid">
        <XCircle className="h-14 w-14 text-destructive mx-auto mb-4" />
        <h1 className="font-display text-3xl font-bold">Bağlantı Kullanılamıyor</h1>
        <p className="text-muted-foreground mt-3">{msg}</p>
        <div className="mt-6 flex gap-3 justify-center">
          <Button asChild variant="outline"><Link to="/sifremi-unuttum">Yeni Talep</Link></Button>
          <Button asChild><Link to="/giris">Giriş Yap</Link></Button>
        </div>
      </div>
    );
  }
  if (done) {
    return (
      <div className="mx-auto max-w-md px-4 py-16 text-center" data-testid="reset-done">
        <CheckCircle2 className="h-14 w-14 text-success mx-auto mb-4" />
        <h1 className="font-display text-3xl font-bold">Şifreniz Güncellendi</h1>
        <p className="text-muted-foreground mt-3">Yönlendiriliyorsunuz…</p>
      </div>
    );
  }
  return (
    <div className="mx-auto max-w-md px-4 py-16" data-testid="reset-page">
      <div className="text-center mb-8">
        <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary mb-3">
          <KeyRound className="h-7 w-7" />
        </div>
        <div className="overline text-accent">Şifre Belirle</div>
        <h1 className="font-display text-3xl font-bold mt-2">Yeni Şifreni Oluştur</h1>
        <p className="text-sm text-muted-foreground mt-2">
          <b>{state.email}</b> adresi için tek kullanımlık bağlantı doğrulandı.
        </p>
      </div>
      <div className="rounded-xl border border-border bg-card p-6 space-y-4">
        <div>
          <Label>Yeni Şifre</Label>
          <Input type="password" value={pwd} onChange={(e) => setPwd(e.target.value)} data-testid="reset-pwd" />
          <div className="text-xs text-muted-foreground mt-1">En az 8 karakter olmalı.</div>
        </div>
        <div>
          <Label>Yeni Şifre (Tekrar)</Label>
          <Input type="password" value={pwd2} onChange={(e) => setPwd2(e.target.value)} data-testid="reset-pwd2" />
        </div>
        <Button onClick={submit} disabled={busy || !pwd || !pwd2} className="w-full" data-testid="reset-submit">
          <ShieldCheck className="h-4 w-4" /> {busy ? "Güncelleniyor…" : "Şifremi Belirle"}
        </Button>
      </div>
    </div>
  );
}
